const OPT_OUT_KEY = 'analytics_opt_out'
const FIRST_SEEN_KEY = 'analytics_first_seen_date'
const LAST_ACTIVE_KEY = 'analytics_last_active_date'
const UTM_KEY = 'analytics_utm' // first-touch campaign tags (localStorage)

const UTM_PARAMS = ['utm_source', 'utm_campaign']
const UTM_TTL_MS = 90 * 24 * 60 * 60 * 1000 // first-touch attribution expires after 90 days

function todayStr() {
  const d = new Date()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${month}-${day}`
}

// Capture UTM tags from the landing URL, first-touch only: the first tagged
// visit wins and is remembered (for up to 90 days), so later untagged
// navigations don't overwrite which campaign brought the user in. Server-side
// these are allow-listed, so we don't over-sanitize here. Call once on app load.
export function captureUtm(search = window.location.search) {
  try {
    const params = new URLSearchParams(search)
    const found = {}
    for (const key of UTM_PARAMS) {
      const value = params.get(key)
      if (value) found[key] = value
    }
    if (Object.keys(found).length > 0 && !storedUtm()) {
      localStorage.setItem(UTM_KEY, JSON.stringify({ ...found, t: Date.now() }))
    }
  } catch {
    // Ignore — measurement is best-effort.
  }
}

// The stored first-touch tags, or null if absent / expired / unparseable.
function storedUtm() {
  try {
    const raw = localStorage.getItem(UTM_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !parsed.t || Date.now() - parsed.t > UTM_TTL_MS) {
      localStorage.removeItem(UTM_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

// X-Utm-* headers from the stored first-touch tags (only those present).
function utmHeaders() {
  const utm = storedUtm() || {}
  const headers = {}
  if (utm.utm_source) headers['X-Utm-Source'] = utm.utm_source
  if (utm.utm_campaign) headers['X-Utm-Campaign'] = utm.utm_campaign
  return headers
}

// Headers attached to a price lookup.
//
// When opted out we send a single explicit marker — X-Visit-Track: 0 — so the
// server records NOTHING for the request, not even anonymous totals. The
// Settings toggle is therefore fully honoured (not just "no identifier").
//
// When opted in, no identifier ever leaves the browser — only two booleans plus
// first-touch campaign tags the server folds into daily aggregate counters:
//   X-Visit-First-Today — first lookup today (drives unique browser-days / DAU)
//   X-Visit-New         — this browser has never been seen before (new vs returning)
//   X-Utm-*             — first-touch campaign attribution
// Calling this marks today active, so repeat lookups the same day aren't recounted.
export function getVisitHeaders() {
  if (isAnalyticsOptedOut()) return { 'X-Visit-Track': '0' }

  const today = todayStr()
  const newVisitor = !localStorage.getItem(FIRST_SEEN_KEY)
  const firstToday = localStorage.getItem(LAST_ACTIVE_KEY) !== today

  if (newVisitor) localStorage.setItem(FIRST_SEEN_KEY, today)
  if (firstToday) localStorage.setItem(LAST_ACTIVE_KEY, today)

  return {
    'X-Visit-First-Today': firstToday ? '1' : '0',
    'X-Visit-New': newVisitor ? '1' : '0',
    ...utmHeaders(),
  }
}

export function setAnalyticsOptOut(optedOut) {
  if (optedOut) {
    localStorage.setItem(OPT_OUT_KEY, 'true')
    // Clear the local visit state so re-enabling starts fresh.
    localStorage.removeItem(FIRST_SEEN_KEY)
    localStorage.removeItem(LAST_ACTIVE_KEY)
  } else {
    localStorage.removeItem(OPT_OUT_KEY)
  }
}

export function isAnalyticsOptedOut() {
  return localStorage.getItem(OPT_OUT_KEY) === 'true'
}
